import { useEffect, useState } from 'react';
import './styles/admin.scss';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { useConfirm } from '../../components/admin/useConfirm';
import { PRODUCTS, PRODUCT_LABEL, PAYMENT } from './adminConstants';
import { siteUrl } from '../../Constants';

type Pricing = { product: number; basePrice: number; discountedPrice?: number | null; available?: boolean; paymentModel: number; firstPartAmount?: number | null; secondPartDueDays?: number | null; monthCount?: number | null };
type Discount = { id: string; code: string; percent: number; active: boolean; products: number[] };
type Installment = { id: string; sequence: number; amount: number; dueDate: string; paid: boolean; paidAt?: string | null; paymentIntentId?: string | null; status: string; failedAt?: string | null; failureReason?: string | null };
type Invoice = { id: string; number: string; amount: number; issuedAt: string };
type Order = { id: string; userEmail?: string | null; product: number; paymentModel: number; paymentMethod: number; variableSymbol?: string | null; totalAmount: number; discountCode?: string | null; discountPercent: number; createdAt: string; installments: Installment[]; invoices: Invoice[] };

const METHOD_LABEL: { [k: number]: string } = { 0: "Kartou", 1: "Převodem" };
const statusClass = (s: string) => s === "Zaplaceno" ? "ok" : s === "Selhalo" ? "no" : "tag";
type SalesSource = { source?: string | null; medium?: string | null; campaign?: string | null; visits: number; orders: number; paidOrders: number; revenue: number };
type LeadSource = { source?: string | null; medium?: string | null; campaign?: string | null; leads: number; consultations: number };

// Ready-made ad links per placement — the source/medium the report reads back. Admin
// copies one and only swaps the campaign name. Keep the labels/values lowercase so they
// group cleanly in the "sales by source" table (same casing the visitor's URL carries).
const AD_LINKS: { label: string; source: string; medium: string }[] = [
    { label: "Facebook – placená reklama", source: "facebook", medium: "paid" },
    { label: "Instagram – placená reklama", source: "instagram", medium: "paid" },
    { label: "Instagram – Stories", source: "instagram", medium: "stories" },
    { label: "Instagram – odkaz v biu", source: "instagram", medium: "bio" },
];
const adLink = (source: string, medium: string) =>
    `${siteUrl}/?utm_source=${source}&utm_medium=${medium}&utm_campaign=nazev-kampane`;

const fmt = (d: string) => { const x = new Date(d); return x.getDate() + ". " + (x.getMonth() + 1) + ". " + x.getFullYear(); };
const num = (v: any) => (v === "" || v == null ? null : Number(v));

export default function AdminCommerce() {
    const [tab, setTab] = useState<"ceny" | "slevy" | "objednavky" | "zdroje">("ceny");
    const [pricing, setPricing] = useState<Pricing[]>([]);
    const [discounts, setDiscounts] = useState<Discount[]>([]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [sources, setSources] = useState<SalesSource[]>([]);
    const [leadSources, setLeadSources] = useState<LeadSource[]>([]);
    const [promo, setPromo] = useState<string>(""); // global promo deadline, yyyy-MM-dd ("" = none)
    const [loaded, setLoaded] = useState(false);
    const [dForm, setDForm] = useState<Discount>({ id: "", code: "", percent: 10, active: true, products: [] });
    const api = new ApiHelper();
    const { ask, confirmModal } = useConfirm();

    useEffect(() => { setLoaded(false); load(); }, [tab]);
    async function load() {
        try {
            if (tab === "ceny") {
                setPricing(await (await api.adminCommerceApi.pricing()).json());
                const p = await (await api.adminCommerceApi.promo()).json();
                setPromo(p.discountValidUntil ? String(p.discountValidUntil).slice(0, 10) : "");
            }
            if (tab === "slevy") setDiscounts(await (await api.adminCommerceApi.discounts()).json());
            if (tab === "objednavky") setOrders(await (await api.adminCommerceApi.orders()).json());
            if (tab === "zdroje") {
                const [salesRes, leadsRes] = await Promise.all([api.adminCommerceApi.salesBySource(), api.adminCommerceApi.consultationsBySource()]);
                setSources(await salesRes.json());
                setLeadSources(await leadsRes.json());
            }
        } catch (x: any) { api.adminError(x); } finally { setLoaded(true); }
    }
    async function savePromo(value: string) {
        setPromo(value);
        try { await api.adminCommerceApi.setPromo({ discountValidUntil: value || null }); api.alert.success(value ? "Akce uložena" : "Akce zrušena"); } catch (x: any) { api.adminError(x); }
    }
    function setPrice(i: number, patch: Partial<Pricing>) { setPricing(pricing.map((p, idx) => idx === i ? { ...p, ...patch } : p)); }
    async function savePrice(p: Pricing) {
        try { await api.adminCommerceApi.setPricing({ product: p.product, basePrice: Number(p.basePrice), discountedPrice: num(p.discountedPrice), available: p.available !== false, paymentModel: p.paymentModel, firstPartAmount: num(p.firstPartAmount), secondPartDueDays: num(p.secondPartDueDays), monthCount: num(p.monthCount) }); api.alert.success("Cena uložena"); } catch (x: any) { api.adminError(x); }
    }
    async function saveDiscount() {
        if (!dForm.code.trim()) { api.alert.error("Zadej kód"); return; }
        const payload = { code: dForm.code.trim(), percent: Number(dForm.percent), active: dForm.active, products: dForm.products };
        try {
            if (dForm.id) await api.adminCommerceApi.updateDiscount(dForm.id, payload);
            else await api.adminCommerceApi.createDiscount(payload);
            api.alert.success("Sleva uložena"); setDForm({ id: "", code: "", percent: 10, active: true, products: [] }); await load();
        } catch (x: any) { api.adminError(x); }
    }
    async function run(p: Promise<any>) { try { await p; await load(); } catch (x: any) { api.adminError(x); } }
    async function copyLink(url: string) {
        try { await navigator.clipboard.writeText(url); api.alert.success("Odkaz zkopírován do schránky"); }
        catch { api.alert.error("Kopírování se nepovedlo"); }
    }

    return (
        <div>
            <div className="adminHead"><div><h1>Obchod</h1><p>Ceny, slevové kódy a objednávky</p></div></div>
            <div className="subTabs">
                <span className={"subTab" + (tab === "ceny" ? " active" : "")} onClick={() => setTab("ceny")}>Ceny</span>
                <span className={"subTab" + (tab === "slevy" ? " active" : "")} onClick={() => setTab("slevy")}>Slevy</span>
                <span className={"subTab" + (tab === "objednavky" ? " active" : "")} onClick={() => setTab("objednavky")}>Objednávky</span>
                <span className={"subTab" + (tab === "zdroje" ? " active" : "")} onClick={() => setTab("zdroje")}>Zdroje</span>
            </div>

            {tab === "ceny" &&
                <>
                <div className="aCard" style={{ marginBottom: "18px" }}>
                    <h2>Časově omezená akce</h2>
                    <p className="hint" style={{ margin: "0 0 12px 0" }}>
                        Do tohoto data platí zvýhodněná cena a na webu i v košíku běží odpočet. Po jeho uplynutí
                        se cena všech produktů vrací na základní („původní") cenu. Nech prázdné pro trvalou platnost.
                    </p>
                    <div className="inlineRow">
                        <div className="aField inline"><label>Zvýhodněná cena platí do</label><input type="date" value={promo} onChange={e => setPromo(e.target.value)} /></div>
                        <div className="priceFoot" style={{ alignSelf: "flex-end" }}>
                            <button className="aBtn" onClick={() => savePromo(promo)}>Uložit</button>
                            {promo && <button className="aBtn ghost" onClick={() => savePromo("")}>Vymazat</button>}
                        </div>
                    </div>
                </div>
                <div className="aGrid">
                    {pricing.map((p, i) => (
                        <div key={p.product} className="aCard priceCard">
                            <div className="priceHead">
                                <h2>{PRODUCT_LABEL[p.product]}</h2>
                                <span className="modelPill">{PAYMENT.find(m => m.v === p.paymentModel)?.l}</span>
                            </div>
                            <div className="inlineRow">
                                <div className="aField inline"><label>Cena (Kč)</label><input type="number" value={p.basePrice} onChange={e => setPrice(i, { basePrice: Number(e.target.value) })} /></div>
                                <div className="aField inline"><label>Sleva. cena</label><input type="number" value={p.discountedPrice ?? ""} onChange={e => setPrice(i, { discountedPrice: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                                <div className="aField inline"><label>Model platby</label><select value={p.paymentModel} onChange={e => setPrice(i, { paymentModel: Number(e.target.value) })}>{PAYMENT.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}</select></div>
                            </div>
                            {p.paymentModel === 1 &&
                                <div className="inlineRow">
                                    <div className="aField inline"><label>1. platba</label><input type="number" value={p.firstPartAmount ?? ""} onChange={e => setPrice(i, { firstPartAmount: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                                    <div className="aField inline"><label>2. spl. za dní</label><input type="number" value={p.secondPartDueDays ?? ""} onChange={e => setPrice(i, { secondPartDueDays: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                                </div>
                            }
                            {p.paymentModel === 2 &&
                                <div className="inlineRow">
                                    <div className="aField inline"><label>Počet měsíců</label><input type="number" value={p.monthCount ?? ""} onChange={e => setPrice(i, { monthCount: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                                </div>
                            }
                            <div className="priceFoot" style={{ justifyContent: "space-between", alignItems: "center" }}>
                                <label className="toggle small" title="Nabízet produkt v prodeji"><input type="checkbox" checked={p.available !== false} onChange={e => setPrice(i, { available: e.target.checked })} /><i className="slider" /> V prodeji</label>
                                <button className="aBtn" onClick={() => savePrice(p)}>Uložit</button>
                            </div>
                        </div>
                    ))}
                </div>
                </>
            }

            {tab === "slevy" &&
                <div>
                    <div className="aCard" style={{ marginBottom: "18px" }}>
                        <h2>{dForm.id ? "Upravit slevu" : "Nová sleva"}</h2>
                        <div className="inlineRow">
                            <div className="aField inline"><label>Kód</label><input value={dForm.code} onChange={e => setDForm({ ...dForm, code: e.target.value })} /></div>
                            <div className="aField inline"><label>Sleva (%)</label><input type="number" value={dForm.percent} onChange={e => setDForm({ ...dForm, percent: Number(e.target.value) })} /></div>
                            <div className="aField inline"><label className="toggle"><input type="checkbox" checked={dForm.active} onChange={e => setDForm({ ...dForm, active: e.target.checked })} /><i className="slider" /> Aktivní</label></div>
                        </div>
                        <div className="aField"><label>Platí pro produkty</label>
                            <div className="checkRow">{PRODUCTS.map(p => (
                                <label key={p.v} className="checkPill"><input type="checkbox" checked={dForm.products.includes(p.v)} onChange={e => setDForm({ ...dForm, products: e.target.checked ? [...dForm.products, p.v] : dForm.products.filter(x => x !== p.v) })} /> {p.l}</label>
                            ))}</div>
                        </div>
                        <div className="aActions"><button className="aBtn" onClick={saveDiscount}>{dForm.id ? "Uložit" : "+ Vytvořit"}</button>{dForm.id && <button className="aBtn ghost" onClick={() => setDForm({ id: "", code: "", percent: 10, active: true, products: [] })}>Zrušit</button>}</div>
                    </div>
                    <table className="aTable">
                        <thead><tr><th>Kód</th><th>Sleva</th><th>Stav</th><th>Produkty</th><th></th></tr></thead>
                        <tbody>
                            {discounts.map(d => (
                                <tr key={d.id} onClick={() => setDForm(d)}>
                                    <td>{d.code}</td><td>{d.percent} %</td>
                                    <td>{d.active ? <span className="ok">aktivní</span> : <span className="muted">neaktivní</span>}</td>
                                    <td>{d.products.length ? d.products.map(p => <span key={p} className="tag">{PRODUCT_LABEL[p]}</span>) : <span className="muted">vše</span>}</td>
                                    <td onClick={e => e.stopPropagation()}><span className="actBtn del" onClick={() => ask(<>Smazat slevový kód „{d.code}"?</>, () => run(api.adminCommerceApi.deleteDiscount(d.id)))}>×</span></td>
                                </tr>
                            ))}
                            {loaded && discounts.length === 0 && <tr><td colSpan={5} className="muted">Žádné slevy.</td></tr>}
                        </tbody>
                    </table>
                </div>
            }

            {tab === "objednavky" &&
                <div className="rowList" style={{ gap: "32px" }}>
                    {orders.map(o => (
                        <div key={o.id} className="aCard">
                            <div className="cardHeadRow">
                                <h2>{PRODUCT_LABEL[o.product]} · {o.totalAmount} Kč</h2>
                                <span className="actBtn del" title="Smazat objednávku" onClick={() => ask(<>Smazat objednávku {PRODUCT_LABEL[o.product]} · {o.totalAmount} Kč ({o.userEmail || "—"})? Smažou se i platby.</>, () => run(api.adminCommerceApi.deleteOrder(o.id)), { title: "Smazat objednávku?" })}>×</span>
                            </div>
                            <p className="hint" style={{ margin: "0 0 12px 0" }}>{o.userEmail || "—"} · {fmt(o.createdAt)} · {PAYMENT.find(p => p.v === o.paymentModel)?.l} · {METHOD_LABEL[o.paymentMethod] ?? "—"}{o.variableSymbol ? " · VS " + o.variableSymbol : ""}{o.discountCode ? " · kód " + o.discountCode + " (−" + o.discountPercent + " %)" : ""}</p>
                            <div className="rowList">
                                {o.installments.map(inst => (
                                    <div key={inst.id} className="lineItem">
                                        <span className="liText">
                                            Platba {inst.sequence} · {inst.amount} Kč · splatnost {fmt(inst.dueDate)}
                                            {" "}<span className={statusClass(inst.status)}>{inst.status}</span>
                                            {inst.failureReason ? <span className="muted" style={{ marginLeft: "6px" }} title={inst.failureReason}>({inst.failureReason})</span> : null}
                                            {inst.paymentIntentId ? <span className="muted" style={{ marginLeft: "6px" }}>· {inst.paymentIntentId}</span> : null}
                                        </span>
                                        <label className="toggle small" title="Označit jako zaplaceno (aktivuje přístup)"><input type="checkbox" checked={inst.paid} onChange={e => run(api.adminCommerceApi.markInstallment(inst.id, e.target.checked))} /><i className="slider" /></label>
                                    </div>
                                ))}
                                {o.installments.length === 0 && <span className="empty">Bez splátek.</span>}
                            </div>
                            {o.invoices && o.invoices.length > 0 &&
                                <div className="rowList" style={{ marginTop: "10px" }}>
                                    {o.invoices.map(inv => (
                                        <div key={inv.id} className="lineItem">
                                            <span className="liText">Faktura {inv.number} · {inv.amount} Kč · {fmt(inv.issuedAt)}</span>
                                            <button className="aBtn ghost" onClick={() => api.downloadAdminInvoice(inv.id, inv.number + ".pdf").catch((x: any) => api.adminError(x))}>Stáhnout</button>
                                        </div>
                                    ))}
                                </div>
                            }
                        </div>
                    ))}
                    {loaded && orders.length === 0 && <p className="muted">Žádné objednávky.</p>}
                </div>
            }

            {tab === "zdroje" &&
                <div>
                    <div className="aCard" style={{ marginBottom: "18px" }}>
                        <h2>Prodej podle zdroje</h2>
                        <p className="hint" style={{ margin: 0 }}>
                            Odkud přišly návštěvy a objednávky. Zdroj se čte z UTM značek v odkazu
                            reklamy (utm_source, utm_campaign…) — u kampaní na Facebooku a Instagramu
                            je nastavíš v odkazu na web. „Přímo/organicky" = návštěva bez UTM značek.
                            <b> Návštěvy</b> = kolik různých lidí zdroj přivedl na web (počítáno
                            anonymně po záložkách prohlížeče), takže vidíš i kampaň, která zatím
                            přivedla návštěvy, ale žádnou objednávku.
                        </p>
                    </div>
                    <div className="aCard" style={{ marginBottom: "18px" }}>
                        <h2>Jaké odkazy vkládat do reklam</h2>
                        <p className="hint" style={{ marginTop: 0 }}>
                            Do reklamy nevkládej obyčejný odkaz na web, ale odkaz s UTM značkami — podle nich
                            se objednávka připíše správnému zdroji v tabulce níže. Zkopíruj hotový odkaz pro
                            dané umístění a v části <code>utm_campaign</code> si jen přepiš název kampaně
                            (malá písmena, bez mezer a diakritiky, např. <code>leden-sleva</code>).
                        </p>
                        <div className="rowList adLinks" style={{ marginTop: "14px" }}>
                            {AD_LINKS.map(l => {
                                const url = adLink(l.source, l.medium);
                                return (
                                    <div className="lineItem" key={l.label}>
                                        <span className="liText">
                                            <strong>{l.label}</strong>
                                            <span className="adUrl">{url}</span>
                                        </span>
                                        <button className="aBtn ghost" onClick={() => copyLink(url)}>Kopírovat</button>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="hint" style={{ marginBottom: 0 }}>
                            Značky: <code>utm_source</code> = kde reklama běží (facebook, instagram),{" "}
                            <code>utm_medium</code> = typ umístění (paid, stories, reels, bio),{" "}
                            <code>utm_campaign</code> = tvůj název kampaně.
                        </p>
                    </div>
                    <table className="aTable">
                        <thead><tr><th>Zdroj</th><th>Médium</th><th>Kampaň</th><th>Návštěvy</th><th>Objednávky</th><th>Zaplacené</th><th>Konverze</th><th>Tržby</th></tr></thead>
                        <tbody>
                            {sources.map((s, i) => (
                                <tr key={i}>
                                    <td>{s.source || <span className="muted">přímo/organicky</span>}</td>
                                    <td>{s.medium || <span className="muted">—</span>}</td>
                                    <td>{s.campaign || <span className="muted">—</span>}</td>
                                    <td>{s.visits}</td>
                                    <td>{s.orders}</td>
                                    <td>{s.paidOrders}</td>
                                    <td>{s.orders ? Math.round((s.paidOrders / s.orders) * 100) : 0} %</td>
                                    <td>{s.revenue} Kč</td>
                                </tr>
                            ))}
                            {loaded && sources.length === 0 && <tr><td colSpan={8} className="muted">Zatím žádná data.</td></tr>}
                        </tbody>
                    </table>

                    <div className="aCard" style={{ margin: "26px 0 18px" }}>
                        <h2>Konzultace a poptávky</h2>
                        <p className="hint" style={{ marginTop: 0 }}>
                            Kolik lidí napsalo přes kontaktní formulář a kolik z nich si vyžádalo{" "}
                            <b>nezávaznou konzultaci</b> — klíčová metrika trychtýře. Zdroj se čte ze
                            stejných UTM značek jako u prodejů.
                        </p>
                        <p style={{ margin: "12px 0 0", fontSize: "1.15em" }}>
                            <b>{leadSources.reduce((a, s) => a + s.consultations, 0)}</b> konzultací
                            {" · "}
                            <b>{leadSources.reduce((a, s) => a + s.leads, 0)}</b> poptávek celkem
                        </p>
                    </div>
                    <table className="aTable">
                        <thead><tr><th>Zdroj</th><th>Médium</th><th>Kampaň</th><th>Poptávky</th><th>Konzultace</th></tr></thead>
                        <tbody>
                            {leadSources.map((s, i) => (
                                <tr key={i}>
                                    <td>{s.source || <span className="muted">přímo/organicky</span>}</td>
                                    <td>{s.medium || <span className="muted">—</span>}</td>
                                    <td>{s.campaign || <span className="muted">—</span>}</td>
                                    <td>{s.leads}</td>
                                    <td>{s.consultations}</td>
                                </tr>
                            ))}
                            {loaded && leadSources.length === 0 && <tr><td colSpan={5} className="muted">Zatím žádná data.</td></tr>}
                        </tbody>
                    </table>
                </div>
            }
            {confirmModal}
        </div>
    );
}
